import React, { Fragment, JSX, ReactNode } from 'react'
import _ from "lodash"
import { Input } from 'reactstrap'
import { CustomInput } from '@src/components/reactstrap-compat'
import { Controller, Control } from 'react-hook-form'
import Select from 'react-select'
import classnames from "classnames"
import Flatpickr from 'react-flatpickr'
import AsyncSelect from 'react-select/async'
//************************************//
import { trans, uuidv4 } from "@utils"
import HelperIcon from "@src/components/helpers/HelperIcon"
import '@styles/react/libs/flatpickr/flatpickr.scss'
//************************************//
import FileInput from "../inputs/FileInput"
import { SelectStyleAction } from "./SelectConfigs"
import UncontrolledContentEditable from "../inputs/UncontrolledContentEditable"
import ContentEditable from "../inputs/ContentEditable"
import { DateTime, UncontrolledDateTime } from "../inputs/date-time"

interface InputContainerProps {
  control?: Control<any>;
  id?: string;
  label?: string | ReactNode;
  type: 'switch' | 'uncontrolled-switch' | 'checkbox' | 'radio' | 'uncontrolledContentEditable' | 'contentEditable' | 'uncontrolled-text' | 'text' | 'uncontrolled-number' | 'number' | 'date' | 'uncontrolled-datetime' | 'controlled-datetime' | 'select' | 'uncontrolled-select' | 'async-select' | 'uncontrolled-async-select' | 'file' | 'uncontrolled-custom-input';
  name?: string;
  as?: any;
  options?: any;
  classNamePrefix?: string;
  className?: string;
  placeholder?: string;
  defaultValue?: any;
  rules?: any;
  isClearable?: boolean;
  isMulti?: boolean;
  inline?: boolean;
  defaultChecked?: boolean;
  hasIcon?: boolean;
  onChangeState?: any;
  onChange?: any;
  innerRef?: any;
  // setValue?: any;
  // unregister?: any;
  onBlur?: any;
  _onEdit?: any;
  style?: React.CSSProperties;
  popUpInfo?: any;
  icon?: any;
  textAreaProps?: any;
  disabled?: boolean;
  checked?: boolean;
  tooltip?: string | ReactNode;
  helperClassName?: string;
  triggerTooltipOnClick?: boolean;
  [key: string]: any; // For any other props that might be passed
}

const InputContainer: React.FC<InputContainerProps> = (props) => {
  const { type, name, tooltip, helperClassName, triggerTooltipOnClick } = props
  const hasTooltip = _.isObject(tooltip) || (_.isString(tooltip) && tooltip.length > 0)

  const renderInput = (): JSX.Element => {
    switch (type) {
      case 'switch':
        return (
          <Controller
            {...props}
            name={name || ''}
            control={props.control}
            as={CustomInput}
            type='switch'
          />
        )
      case 'uncontrolled-switch':
        return (
          <CustomInput
            {...props}
            type='switch'
          />
        )
      case 'checkbox':
        return (
          <CustomInput
            {...props}
            label={(
              <Fragment>
                {props.label && (<span className={'checkbox-label'}>{props.label}</span>)}
                {hasTooltip && (
                  <HelperIcon className={classnames(helperClassName, 'vertical-align-baseline')} name={name}
                    text={tooltip} />
                )}
              </Fragment>
            )}
            className={classnames(`${props.className} input-container-checkbox`)}
          >
          </CustomInput>
        )
      case 'radio':
        return (
          <CustomInput
            {...props}
            label={(
              <Fragment>
                <span className={'checkbox-label'}>{props.label}</span>
                {hasTooltip && (
                  <HelperIcon triggerTooltipOnClick={triggerTooltipOnClick}
                    className={classnames(helperClassName, 'vertical-align-baseline')} name={uuidv4()}
                    text={tooltip} />
                )}
              </Fragment>
            )}
            className={classnames(`${props.className} input-container-checkbox`)}
          >
          </CustomInput>
        )
      case 'uncontrolledContentEditable':
        return (
          <UncontrolledContentEditable
            {...props}
          />
        )
      case 'contentEditable':
        return (
          <ContentEditable
            {...props}
          />
        )
      case 'uncontrolled-text':
        return (
          // @ts-ignore
          <Input
            {...props}
          />
        )
      case 'text':
        return (
          <Controller
            {...props}
            name={name || ''}
            control={props.control}
            as={Input}
            type={'text'}
          />
        )
      case 'uncontrolled-number':
        return (
          <Input
            {...props}
            type={'number'}
          />
        )
      case 'number':
        return (
          <Controller
            {...props}
            name={name || ''}
            control={props.control}
            as={Input}
            type={'number'}
          />
        )
      case 'date':
        return (
          <Flatpickr
            {...props}
          />
        )
      case 'uncontrolled-datetime':
        return (
          // @ts-ignore
          <DateTime
            {...props}
          />
        )
      case 'controlled-datetime':
        return (
          // @ts-ignore
          <UncontrolledDateTime
            {...props}
          />
        )
      case 'select':
        return (
          <Controller
            placeholder={trans('gen.reactSelect.placeholder')}
            {...props}
            name={name || ''}
            control={props.control}
            as={Select}
            noOptionsMessage={() => trans('gen.reactSelect.noOptions')}
          />
        )
      case 'uncontrolled-select':
        return (
          <Select
            className='react-select'
            classNamePrefix='select'
            placeholder={trans('gen.reactSelect.placeholder')}
            noOptionsMessage={() => trans('gen.reactSelect.noOptions')}
            styles={SelectStyleAction(props)}
            {...props}
          />
        )
      case 'async-select':
        return (
          <Controller
            placeholder={trans('gen.reactSelect.placeholder')}
            styles={SelectStyleAction(props)}
            {...props}
            name={name || ''}
            control={props.control}
            as={AsyncSelect}
          />
        )
      case 'uncontrolled-async-select':
        return (
          <AsyncSelect
            className='react-select'
            // classNamePrefix='select'
            placeholder={trans('gen.reactSelect.placeholder')}
            noOptionsMessage={() => trans('gen.reactSelect.noOptions')}
            styles={SelectStyleAction(props)}
            {...props}
          />
        )
      case 'file':
        return (
          <FileInput
            {...props}
          />
        )
      case 'uncontrolled-custom-input':
        return (
          <Controller
            {...props}
            name={name || ''}
            control={props.control}
            as={props.as}
          />
        )
      default:
        return <></>
    }
  }

  return (
    <>
      {renderInput()}
      {type !== 'checkbox' && type !== 'radio' && hasTooltip &&
        <HelperIcon className={helperClassName} name={name} text={tooltip} />
      }
    </>
  )
}

export default InputContainer
